import IconWithPopover from '@components/IconWithPopover/IconWithPopover'
import { RHFCheckbox, RHFTextField } from '@components/RHFInputs'
import { Divider, MenuItem, Stack, Typography } from '@mui/material'
import { getCurrencyName } from '@utils/stringHelpers'
import { UseFormReturn, useWatch } from 'react-hook-form'

import { chooseColor, useBasketCurrency } from './BasketSettings.utils'

type Props = {
	parentForm?: UseFormReturn<any>
}

/**
 * Fields controlling when matched products require review
 */
export default function RequireReview({ parentForm = null }: Props) {
	const currency = useBasketCurrency(parentForm)
	const amountLabel = getCurrencyName(currency, 2).name
	const currencyName = getCurrencyName(currency).name.toLowerCase()

	const [savingsMoreThanType, caseQty] = useWatch({
		name: ['savingsMoreThan.type', 'caseQty.enabled'],
	})

	return (
		<>
			<Typography variant="h6" sx={{ pt: 2 }}>
				Require Review When
			</Typography>

			<Divider />

			{/* Savings Amount */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<Typography variant="body2">The savings variance is ±</Typography>
				<RHFTextField
					name="savingsMoreThan.value"
					type="number"
					size="small"
					sx={{ width: '6rem', mr: 2 }}
					slotProps={{
						input: {
							inputProps: { 'data-testid': 'settings-savings-amount' },
						},
					}}
				/>
				<RHFTextField
					name="savingsMoreThan.type"
					size="small"
					select
					sx={{ width: '10rem' }}
					data-testid="settings-savings-amount-type"
				>
					<MenuItem
						key="PERCENT"
						value="PERCENT"
						data-testid="settings-savings-amount-type-percent"
					>
						Percent
					</MenuItem>
					<MenuItem key="AMOUNT" value="AMOUNT" data-testid="settings-savings-amount-type-amount">
						{amountLabel}
					</MenuItem>
				</RHFTextField>

				<Typography variant="body2">
					{savingsMoreThanType === 'AMOUNT' ? 'of per-case savings' : 'of the total savings'}
				</Typography>

				<IconWithPopover>
					Products are flagged for review if savings fall outside the defined
					<br />
					{`percentage/${currencyName} variance from the target.`}
					<br />( 🏳️ Over Savings Limit)
				</IconWithPopover>
			</Stack>

			{/* Packsize */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<Typography variant="body2">Pack conversion is less than</Typography>
				<RHFTextField
					name="packSize.low"
					type="number"
					size="small"
					sx={{ width: '5rem' }}
					slotProps={{
						input: {
							inputProps: { 'data-testid': 'settings-pack-size-low' },
						},
					}}
				/>
				<Typography variant="body2">or greater than</Typography>
				<RHFTextField
					name="packSize.high"
					type="number"
					size="small"
					sx={{ width: '5rem' }}
					slotProps={{
						input: { inputProps: { 'data-testid': 'settings-pack-size-high' } },
					}}
				/>
				<IconWithPopover>
					If the pack size conversion is less than the low value <br />
					or greater than the high value, it will require a review.
					<br />( 🚩 Bad Pack Conversion)
				</IconWithPopover>
			</Stack>

			{/* Case QTY */}
			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="caseQty.enabled" data-testid="settings-case-qty" />
				<Typography variant="body2" color={chooseColor(caseQty)}>
					Qty more than
				</Typography>
				<RHFTextField
					name="caseQty.value"
					disabled={!caseQty}
					type="number"
					size="small"
					sx={{ width: '6rem' }}
					slotProps={{
						input: { inputProps: { 'data-testid': 'settings-case-qty-value' } },
					}}
				/>
				<Typography variant="body2" color={chooseColor(caseQty)}>
					per week, not stocked, and no direct deal
				</Typography>
				<IconWithPopover>
					When checked, if a matched product is not stocked, <br />
					the customer purchases more than the specified value per week, <br />
					and the customer does not have a direct deal, <br />
					it will require a review.
					<br />( 🚩 High Volume MFG)
				</IconWithPopover>
			</Stack>
		</>
	)
}
